'use client';

import { useState } from 'react';
import ProductCard from './ProductCard';
import EcosystemDiagram from './EcosystemDiagram';
import ComparisonMatrix from './ComparisonMatrix';
import DemoSandbox from './DemoSandbox';
import TechnicalSpecsDownload from './TechnicalSpecsDownload';
import ROICalculator from './ROICalculator';
import LiveChatWidget from './LiveChatWidget';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  color: string;
  features: string[];
  useCases: string[];
}

interface Connection {
  from: string;
  to: string;
  label: string;
}

interface Feature {
  category: string;
  features: {
    name: string;
    knowmax: boolean | string;
    phygitalmax: boolean | string;
    leapmax: boolean | string;
    devicemax: boolean | string;
  }[];
}

interface DemoFeature {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface Resource {
  id: string;
  title: string;
  type: string;
  size: string;
  icon: string;
  description: string;
  downloadUrl: string;
}

interface ProductSuiteInteractiveProps {
  products: Product[];
  connections: Connection[];
  comparisonFeatures: Feature[];
  demoFeatures: Record<string, DemoFeature[]>;
  technicalResources: Resource[];
}

export default function ProductSuiteInteractive({
  products,
  connections,
  comparisonFeatures,
  demoFeatures,
  technicalResources,
}: ProductSuiteInteractiveProps) {
  const [activeDemoProduct, setActiveDemoProduct] = useState<string | null>(null);
  const [activeROIProduct, setActiveROIProduct] = useState<string | null>(null);

  const handleDemoClick = (productId: string) => {
    setActiveDemoProduct(productId);
    const demoSection = document.getElementById('demo-sandbox');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleLearnMore = (productId: string) => {
    const comparisonSection = document.getElementById('comparison-matrix');
    if (comparisonSection) {
      comparisonSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleROICalculation = (productId: string) => {
    setActiveROIProduct(productId);
    const roiSection = document.getElementById('roi-calculator');
    if (roiSection) {
      roiSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="space-y-16">
      {/* Products Grid */}
      <section>
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-4xl text-primary mb-4">
            Our Product Suite
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
            Four powerful products working together to transform your customer engagement strategy
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onDemoClick={handleDemoClick}
              onLearnMore={handleLearnMore}
            />
          ))}
        </div>
      </section>

      {/* Ecosystem Diagram */}
      <section>
        <EcosystemDiagram connections={connections} />
      </section>

      {/* Comparison Matrix */}
      <section id="comparison-matrix">
        <ComparisonMatrix features={comparisonFeatures} />
      </section>

      {/* Demo Sandbox */}
      {activeDemoProduct && (
        <section id="demo-sandbox">
          <DemoSandbox
            productId={activeDemoProduct}
            productName={products.find(p => p.id === activeDemoProduct)?.name || ''}
            demoFeatures={demoFeatures[activeDemoProduct] || []}
          />
        </section>
      )}

      {/* ROI Calculator */}
      <section id="roi-calculator">
        <ROICalculator
          productName={
            activeROIProduct
              ? products.find(p => p.id === activeROIProduct)?.name || 'UnifyMax Suite'
              : 'UnifyMax Suite'
          }
        />
      </section>

      {/* Technical Resources */}
      <section>
        <TechnicalSpecsDownload resources={technicalResources} />
      </section>

      {/* Live Chat Widget */}
      <LiveChatWidget />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-primary to-brand-teal rounded-lg p-12 text-center text-white">
        <h2 className="font-display font-bold text-3xl mb-4">
          Ready to Transform Your Customer Engagement?
        </h2>
        <p className="font-body text-lg mb-8 opacity-90 max-w-2xl mx-auto">
          Schedule a personalized demo with our technical specialists and discover how UnifyMax can drive measurable results for your organization.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button className="px-8 py-3 bg-conversion text-conversion-foreground rounded-md font-cta text-base hover:shadow-cta transition-all duration-300">
            Schedule Technical Consultation
          </button>
          <button className="px-8 py-3 bg-white text-primary rounded-md font-cta text-base hover:shadow-md transition-all duration-300">
            Download Product Brochure
          </button>
        </div>
      </section>
    </div>
  );
}